import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";
import { ArrowLeft, FileSpreadsheet, Printer, Inbox, FileDown, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { PaymentRegister } from "@/components/PaymentRegister";

const n2 = (v) => (Number(v) || 0).toFixed(2);
const inr = (v) => `₹ ${n2(v)}`;
const sum = (rows, fn) => rows.reduce((a, r) => a + (fn(r) || 0), 0);

const toWords = (num) => {
  num = Math.round(Math.abs(Number(num) || 0));
  if (num === 0) return "Zero";
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (n) => (n < 20 ? a[n] : b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : ""));
  const three = (n) => (n >= 100 ? a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + two(n % 100) : "") : two(n));
  let out = "";
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  if (crore) out += three(crore) + " Crore ";
  if (lakh) out += two(lakh) + " Lakh ";
  if (thousand) out += two(thousand) + " Thousand ";
  if (num) out += three(num);
  return out.trim();
};

const TH = "py-3 px-3 text-left font-medium text-[13px] whitespace-nowrap border-r border-white/20 last:border-r-0";
const TD = "py-3.5 px-3 border-r border-slate-200 last:border-r-0 align-middle";

const NoData = () => (
  <div className="py-10 flex flex-col items-center gap-1.5 text-slate-400">
    <Inbox className="h-9 w-9 stroke-1" />
    <span className="text-xs">No data</span>
  </div>
);

const Heading = ({ children }) => <h3 className="text-sm font-semibold text-slate-800 mb-2.5 mt-1">{children}</h3>;

const commOf = (r) => (r.quantity || 0) * ((r.company_rate || 0) - (r.market_rate || 0));
const pctOf = (r) => (r.company_rate ? Math.round((((r.company_rate || 0) - (r.market_rate || 0)) / r.company_rate) * 1000) / 10 : 0);
const totalOf = (r) => (r.quantity || 0) * (r.company_rate || 0);

function DispatchTable({ trips, extra }) {
  const cols = [
    ["Sl No"], ["Material Type"], ["GR No"], ["GR Date"], ["Load Location"], ["Delivery Location"], ["Vehicle Number"], ["Vehicle Type"],
    ["Delivery Number"], ["Quantity"], ["Company Rate"], ["Market Rate"], ["Total"], ["Commission"], ["Diesel"], ["Cash"], ["Bank Transfer"],
    ...extra.map((e) => [e.label]), ["Shortage"],
  ];
  return (
    <div className="rounded-lg border border-slate-200 overflow-x-auto" data-testid="os-dispatch">
      <table className="w-full text-[13px] text-slate-700">
        <thead className="bg-slate-600 text-white"><tr>{cols.map(([l]) => <th key={l} className={TH}>{l}</th>)}</tr></thead>
        <tbody>
          {trips.map((r, i) => (
            <tr key={r.id || i} className="border-t border-slate-200 even:bg-slate-50/60">
              <td className={TD}>{i + 1}</td>
              <td className={`${TD} uppercase`}>{r.material_type || "-"}</td>
              <td className={TD}>{r.gr_no || ""}</td>
              <td className={`${TD} whitespace-nowrap`}>{fmtDate(r.gr_date)}</td>
              <td className={TD}>{r.load_location || "-"}</td>
              <td className={TD}>{r.delivery_location || "-"}</td>
              <td className={`${TD} whitespace-nowrap`}>{r.vehicle_number}</td>
              <td className={TD}>{r.vehicle_type || "-"}</td>
              <td className={TD}>{r.delivery_number || "-"}</td>
              <td className={TD}>{r.quantity || 0}</td>
              <td className={TD}>{r.company_rate || 0}</td>
              <td className={TD}>{r.market_rate || 0}</td>
              <td className={TD}>{n2(totalOf(r))}</td>
              <td className={`${TD} whitespace-nowrap`}><span className="inline-block w-12 text-center">{pctOf(r)} %</span><span className="ml-3">{n2(commOf(r))}</span></td>
              <td className={TD}>{r.diesel || 0}</td>
              <td className={TD}>{r.cash || 0}</td>
              <td className={TD}>{r.bank_transfer || 0}</td>
              {extra.map((e) => <td key={e.key} className={TD}>{r[e.key] || 0}</td>)}
              <td className={TD}>{r.shortage || 0}</td>
            </tr>
          ))}
          {trips.length === 0 && <tr><td colSpan={cols.length}><NoData /></td></tr>}
        </tbody>
        {trips.length > 0 && (
          <tfoot className="border-t border-slate-200 font-medium">
            <tr>
              <td colSpan={9} className="py-3 px-3 text-right">Total</td>
              <td className="py-3 px-3" data-testid="os-total-qty">{n2(sum(trips, (r) => r.quantity)).replace(/\.?0+$/, "")}</td>
              <td colSpan={2}></td>
              <td className="py-3 px-3" data-testid="os-total-amount">{n2(sum(trips, totalOf))}</td>
              <td className="py-3 px-3 text-center" data-testid="os-total-commission">{n2(sum(trips, commOf))}</td>
              <td className="py-3 px-3">{n2(sum(trips, (r) => r.diesel))}</td>
              <td className="py-3 px-3">{n2(sum(trips, (r) => r.cash))}</td>
              <td className="py-3 px-3">{n2(sum(trips, (r) => r.bank_transfer))}</td>
              {extra.map((e) => <td key={e.key} className="py-3 px-3">{n2(sum(trips, (r) => r[e.key]))}</td>)}
              <td className="py-3 px-3">{n2(sum(trips, (r) => r.shortage))}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function AdvanceTable({ rows, outstanding }) {
  return (
    <div data-testid="os-advance">
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-[13px] text-slate-700">
          <thead className="bg-slate-600 text-white"><tr>{["Sl No", "Date", "Narration", "Debit", "Credit"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-200">
                <td className={TD}>{i + 1}</td><td className={TD}>{fmtDate(r.date)}</td><td className={TD}>{r.narration}</td>
                <td className={TD}>{r.debit ? n2(r.debit) : ""}</td><td className={TD}>{r.credit ? n2(r.credit) : ""}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5}><NoData /></td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex items-center justify-between bg-slate-100 px-10 py-3.5 text-sm font-semibold text-sky-700">
        <span>Total Outstanding Debit</span><span data-testid="os-outstanding">{outstanding ? n2(outstanding) : 0}</span>
      </div>
    </div>
  );
}

function VoucherTable({ rows }) {
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="os-voucher">
      <table className="w-full text-[13px] text-slate-700">
        <thead className="bg-slate-600 text-white"><tr>{["Sl No", "Date", "Narration", "Vehicle Number", "Amount"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((v, i) => (
            <tr key={v.id || i} className="border-t border-slate-200">
              <td className={TD}>{i + 1}</td><td className={`${TD} whitespace-nowrap`}>{fmtDate(v.voucher_date)}</td>
              <td className={TD}>{v.narration || v.mode_of_payment || "-"}</td><td className={TD}>{v.vehicle_number || "-"}</td>
              <td className={TD}>{n2(v.amount)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5}><NoData /></td></tr>}
        </tbody>
        {rows.length > 0 && <tfoot className="border-t border-slate-200 font-medium"><tr><td colSpan={4} className="py-3 px-3 text-right">Total</td><td className="py-3 px-3">{n2(sum(rows, (v) => v.amount))}</td></tr></tfoot>}
      </table>
    </div>
  );
}

function ExpenseCard({ lines, total, paid }) {
  const bal = total - paid;
  return (
    <div className="rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="os-expense">
      <div className="flex justify-between px-4 py-3 bg-slate-50 font-semibold text-slate-800"><span>Particulars</span><span>Amount</span></div>
      {lines.map(([l, v]) => (
        <div key={l} className="flex justify-between px-4 py-3 border-t border-slate-100 text-[13px] text-slate-700"><span>{l}</span><span>{inr(v)}</span></div>
      ))}
      <div className="flex justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm font-semibold text-sky-700" data-testid="os-net-total"><span>Total</span><span>{inr(total)}</span></div>
      <div className="px-4 pb-3 bg-slate-50 text-[11px] italic text-slate-500">{total < 0 ? "Minus " : ""}Rupees {toWords(total)} Only</div>
      <div className="flex justify-between px-4 py-3 border-t border-slate-200 text-[13px] text-slate-700" data-testid="os-payments-made"><span>Payments Made</span><span>{inr(paid)}</span></div>
      <div className={`flex justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm font-semibold ${bal < 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid="os-balance-payable"><span>Balance Payable</span><span>{inr(bal)}</span></div>
      <div className="px-4 pb-3 bg-slate-50 text-[11px] italic text-slate-500">{bal < 0 ? "Minus " : ""}Rupees {toWords(bal)} Only</div>
    </div>
  );
}

const exportXls = (owner, node) => {
  node = node.cloneNode(true);
  node.querySelectorAll(".no-print").forEach((el) => el.remove());
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><h3>${owner} Performance Report</h3>${[...node.querySelectorAll("[data-xls]")].map((s) => `<h4>${s.dataset.xls}</h4>${s.querySelector("table")?.outerHTML || s.innerHTML}`).join("<br/>")}</body></html>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${owner.replace(/\s+/g, "_")}_Performance_Report.xls`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const printNode = (owner, node) => {
  const w = window.open("", "_blank", "width=1200,height=900");
  if (!w) return toast.error("Allow pop-ups to print");
  const styles = [...document.querySelectorAll("style, link[rel='stylesheet']")].map((s) => s.outerHTML).join("");
  w.document.write(`<html><head><title>${owner} Performance Report</title>${styles}<style>@page{size:A4 landscape;margin:10mm}body{background:#fff;padding:12px}.no-print{display:none!important}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}.overflow-x-auto,.overflow-hidden{overflow:visible!important}</style></head><body>${node.outerHTML}</body></html>`);
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 600);
};

// Render the statement off-screen at a fixed width so the PDF looks the same on phone and desktop.
const buildPdf = async (owner, period, hubName, node) => {
  const wrap = document.createElement("div");
  wrap.style.cssText = "width:1400px;background:#fff;color:#0f172a;padding:24px";
  const holder = document.createElement("div");
  holder.style.cssText = "position:absolute;left:0;top:0;z-index:-1;pointer-events:none";
  holder.appendChild(wrap);
  wrap.innerHTML = `<div style="display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2px solid #0f172a;padding-bottom:10px;margin-bottom:16px"><div><div style="font-size:22px;font-weight:700">${owner}</div><div style="font-size:13px;color:#475569">Owner Performance Statement${hubName ? ` · ${hubName}` : ""}</div></div><div style="font-size:13px;color:#475569;text-align:right">Period: <b>${period}</b><br/>Generated: ${new Date().toLocaleDateString("en-IN")}</div></div>`;
  const clone = node.cloneNode(true);
  clone.querySelectorAll(".no-print").forEach((el) => el.remove());
  clone.querySelectorAll(".overflow-x-auto,.overflow-hidden,.overflow-y-auto").forEach((el) => { el.style.overflow = "visible"; });
  wrap.appendChild(clone);
  document.body.appendChild(holder);
  const w = Math.max(1400, ...[...clone.querySelectorAll("table")].map((t) => t.scrollWidth + 48));
  wrap.style.width = `${w}px`;
  try {
    const scale = 1.6;
    const top0 = wrap.getBoundingClientRect().top;
    // Safe page-break points: bottom edge of every row / block, in canvas px.
    const cuts = [...wrap.querySelectorAll("tr, section, [data-testid='os-expense'] > div")]
      .map((el) => (el.getBoundingClientRect().bottom - top0) * scale).sort((a, b) => a - b);
    const canvas = await html2canvas(wrap, { scale, useCORS: true, backgroundColor: "#ffffff", windowWidth: w + 50 });
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
    const m = 6, pw = 297 - 2 * m, ph = 210 - 2 * m;
    const pagePx = Math.floor(canvas.width * ph / pw);
    let y = 0, first = true;
    while (y < canvas.height - 2) {
      let end = Math.min(y + pagePx, canvas.height);
      if (end < canvas.height) { const c = cuts.filter((v) => v > y + pagePx * 0.5 && v <= end).pop(); if (c) end = Math.ceil(c); }
      const slice = document.createElement("canvas");
      slice.width = canvas.width; slice.height = end - y;
      slice.getContext("2d").drawImage(canvas, 0, y, canvas.width, end - y, 0, 0, canvas.width, end - y);
      if (!first) pdf.addPage();
      pdf.addImage(slice.toDataURL("image/jpeg", 0.85), "JPEG", m, m, pw, (end - y) * pw / canvas.width);
      first = false; y = end;
    }
    return pdf.output("blob");
  } finally { document.body.removeChild(holder); }
};

const pdfName = (owner, period) => `${owner.replace(/\s+/g, "_")}_Statement_${period.replace(/[^\w-]+/g, "_")}.pdf`;

function PdfButtons({ owner, period, hubName, nodeRef, disabled }) {
  const [busy, setBusy] = useState(false);
  const canShare = typeof navigator !== "undefined" && !!navigator.canShare;
  const run = async (share) => {
    setBusy(true);
    try {
      const blob = await buildPdf(owner, period, hubName, nodeRef.current);
      const name = pdfName(owner, period);
      const file = new File([blob], name, { type: "application/pdf" });
      if (share && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `${owner} Statement`, text: `${owner} statement · ${period}` });
      } else {
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href);
        toast.success("PDF downloaded — attach it in WhatsApp");
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not create PDF"); }
    setBusy(false);
  };
  return (
    <>
      <Button className="h-10 bg-rose-600 hover:bg-rose-600/90 text-white gap-2" disabled={disabled || busy} onClick={() => run(false)} data-testid="os-download-pdf">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}Download PDF
      </Button>
      {canShare && <Button className="h-10 bg-emerald-600 hover:bg-emerald-600/90 text-white gap-2" disabled={disabled || busy} onClick={() => run(true)} data-testid="os-share-pdf"><Share2 className="h-4 w-4" />Share</Button>}
    </>
  );
}

export function OwnerStatementDialog({ owner, filters, onClose }) {
  const { selectedHub, year, hubs } = useApp();
  const hubName = selectedHub && selectedHub !== "all" ? (hubs || []).find((h) => h.id === selectedHub)?.name : "All Hubs";
  const [rep, setRep] = useState(null);
  const ref = useRef(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const params = { owner, hub: selectedHub, year };
    if (filters.month !== "all") params.month = filters.month;
    if (filters.start) params.start = filters.start;
    if (filters.end) params.end = filters.end;
    api.get("/owner-report", { params }).then(({ data }) => setRep(data)).catch(() => toast.error("Could not load owner report"));
  }, [owner, selectedHub, year, filters, tick]);

  const period = filters.start || filters.end ? `${filters.start || "…"} → ${filters.end || "…"}` : (filters.month !== "all" ? `${year}-${String(filters.month).padStart(2, "0")}` : `Year ${year}`);
  const trips = rep?.trips || [];
  const extra = [["fastag", "FASTag"], ["unloading_amount", "Unloading"]].filter(([k]) => trips.some((r) => r[k])).map(([key, label]) => ({ key, label }));
  const adv = [
    ...(rep?.advances || []).map((a) => ({ date: a.entry_date, narration: "Advance", debit: a.outstanding_amount || 0, credit: 0 })),
    ...(rep?.recoveries || []).map((r) => ({ date: r.recovery_date, narration: `Recovery${r.mode_of_payment ? ` · ${r.mode_of_payment}` : ""}`, debit: 0, credit: r.amount || 0 })),
  ].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const outstanding = sum(adv, (r) => r.debit) - sum(adv, (r) => r.credit);
  const vouchers = rep?.vouchers || [];
  const lines = [
    ["Total amount", sum(trips, totalOf)], ["Commision", sum(trips, commOf)], ["Diesel", sum(trips, (r) => r.diesel)],
    ["Cash", sum(trips, (r) => r.cash)], ["Bank Transfer", sum(trips, (r) => r.bank_transfer)],
    ...extra.map((e) => [e.label, sum(trips, (r) => r[e.key])]),
    ["Shortage", sum(trips, (r) => r.shortage)], ["Outstanding Debit", outstanding], ["Advance Voucher", sum(vouchers, (v) => v.amount)],
    ...(rep?.summary?.adblue ? [["AdBlue", rep.summary.adblue]] : []),
    ...(rep?.summary?.expenses ? [["Other Expenses", rep.summary.expenses]] : []),
  ];
  const net = lines[0][1] - lines.slice(1).reduce((a, [, v]) => a + (v || 0), 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[98vw] w-[98vw] h-[96vh] p-0 gap-0 bg-white text-slate-900 overflow-hidden flex flex-col [&>button]:hidden" data-testid="owner-report-dialog">
        <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <button onClick={onClose} className="mt-1 text-slate-600 hover:text-slate-900" data-testid="os-back"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <DialogTitle className="text-[15px] font-semibold text-slate-900">{owner} Performance Report</DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">Print and download the Report · {period}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <PdfButtons owner={owner} period={period} hubName={hubName} nodeRef={ref} disabled={!rep} />
            <Button variant="outline" size="icon" className="h-10 w-10 bg-white border-slate-200 text-slate-700 hover:bg-slate-50" disabled={!rep} onClick={() => exportXls(owner, ref.current)} title="Download Excel" data-testid="os-download-excel"><FileSpreadsheet className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-10 w-10 bg-white border-slate-200 text-slate-700 hover:bg-slate-50" disabled={!rep} onClick={() => printNode(owner, ref.current)} title="Print" data-testid="os-print"><Printer className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!rep ? <div className="py-24 text-center text-slate-400">Loading…</div> : (
            <div ref={ref} className="space-y-5 bg-white text-slate-900">
              <div className="hidden print:block text-base font-semibold">{owner} Performance Report · {period}</div>
              <section data-xls="Dispatch"><Heading>Dispatch</Heading><DispatchTable trips={trips} extra={extra} /></section>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-5 items-start">
                <div className="space-y-5">
                  <section data-xls="Advance"><Heading>Advance</Heading><AdvanceTable rows={adv} outstanding={outstanding} /></section>
                  <section data-xls="Voucher"><Heading>Voucher</Heading><VoucherTable rows={vouchers} /></section>
                </div>
                <div className="space-y-5">
                  <section data-xls="Expense"><Heading>Expense</Heading><ExpenseCard lines={lines} total={net} paid={sum(rep.payments || [], (p) => p.amount)} /></section>
                  <section data-xls="Payment Register"><PaymentRegister owner={owner} hubId={selectedHub} rows={rep.payments || []} onChanged={() => setTick((t) => t + 1)} /></section>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
